"use client";

import React from "react";
import { AlertCircle, BadgeDollarSign, BriefcaseBusiness, CircleCheck, ClipboardCheck, FileText, GraduationCap, Mail, MapPin, NotebookTabs } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const focusAreas = [{
  title: "Client Follow-Through",
  description: "Track outstanding items, reporting requirements, deadlines, and next steps across assigned client files."
}, {
  title: "Financial File Oversight",
  description: "Review coding for consistency, identify missing information or unusual activity, and coordinate corrections using established processes."
}, {
  title: "Reporting and Funding Support",
  description: "Gather information from client staff, track reporting calendars, and support funding reports and related documentation."
}, {
  title: "Process Documentation",
  description: "Prepare clear checklists, guides, workflow tools, and training materials so recurring work can be completed more consistently."
}, {
  title: "Risk and Issue Monitoring",
  description: "Flag delays, discrepancies, recurring issues, or missing information early so they can be reviewed and addressed."
}, {
  title: "Cross-Client Improvement",
  description: "Identify trends across client files and support internal discussions about process improvements and training needs."
}];
const successProfile = ["Proactive and comfortable following up until work is completed.", "Comfortable working with incomplete or changing information.", "Able to recognize when something does not look right and investigate.", "Able to distinguish between execution errors and underlying process issues.", "Comfortable escalating issues that may affect funding, reporting, compliance, or defined processes.", "Organized, respectful, and able to manage multiple priorities."];
const requiredExperience = ["5 to 10 years of experience in bookkeeping, financial operations, client financial support, or similar work.", "Experience working directly with clients to gather financial or reporting information.", "Strong understanding of how transactions flow through financial statements.", "Strong Excel skills.", "Experience managing multiple client files or projects at the same time."];
const strongAssets = ["Experience supporting First Nation, public sector, or funded program reporting.", "Experience identifying errors or inconsistencies in financial data.", "Experience working in high-volume or deadline-driven environments.", "Experience documenting processes or developing training materials.", "Operational or project coordination experience."];
export default function ServiceDetailsSealantRepair() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="252481" id="job-details-part-1" className="bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-14 transition-all duration-700 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="secondary" className="mb-4 text-secondary bg-secondary/10 border-transparent">
            Position Summary
          </Badge>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-primary-500 mb-6">
            Client Financial Administration Lead
          </h2>

          <p className="text-lg text-muted-foreground font-default leading-relaxed">
            RiverBend is seeking a Client Financial Administration Lead to
            support client financial operations, reporting coordination, and
            administrative follow-through across multiple files. This is an
            execution-focused role for someone who can keep requirements on
            track, identify issues early, and help client administrative staff
            complete financial and reporting tasks accurately and on time.
          </p>
        </div>

        <div ref={contentRef} className={`grid grid-cols-1 lg:grid-cols-3 gap-8 xl:gap-12 transition-all duration-700 delay-100 transform ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-2 space-y-10 min-w-0">
            <div>
              <h3 className="text-2xl font-default font-bold text-primary-500 mb-5 flex items-center gap-2">
                <FileText className="text-secondary size-6" />
                What this role focuses on
              </h3>

              <p className="text-muted-foreground font-default leading-relaxed mb-6">
                This role works closely with client administrators and
                RiverBend team members to track outstanding items, coach clients
                through required steps, monitor coding and budget trends, and
                resolve gaps in information so reporting and financial work can
                move forward smoothly. It also includes documenting processes
                and developing practical training tools to support consistency
                across clients and internal teams.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {focusAreas.map((item, i) => <div key={i} data-index={i} className="bg-primary-50/70 rounded-md p-5">
                    <div className="flex items-start gap-3 mb-2">
                      <CircleCheck className="size-5 text-secondary shrink-0 mt-0.5" />
                      <h4 className="font-default font-bold text-primary-500">
                        {item.title}
                      </h4>
                    </div>

                    <p className="text-muted-foreground font-default text-sm leading-relaxed pl-8">
                      {item.description}
                    </p>
                  </div>)}
              </div>
            </div>

            <Separator className="bg-border" />

            <div>
              <h3 className="text-2xl font-default font-bold text-primary-500 mb-5 flex items-center gap-2">
                <ClipboardCheck className="text-secondary size-6" />
                Success Profile
              </h3>

              <p className="text-muted-foreground font-default mb-6 leading-relaxed">
                This role is a strong fit for someone who is organized,
                persistent, practical, and comfortable working through incomplete
                or evolving information. The successful candidate should be able
                to follow up respectfully, investigate issues, escalate when
                needed, and translate complex or inconsistent processes into
                clear steps.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {successProfile.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-3">
                    <CircleCheck className="size-5 text-secondary shrink-0 mt-0.5" />
                    <span className="text-foreground font-medium font-default">
                      {item}
                    </span>
                  </div>)}
              </div>
            </div>

            <Separator className="bg-border" />

            <div>
              <h3 className="text-2xl font-default font-bold text-primary-500 mb-5 flex items-center gap-2">
                <NotebookTabs className="text-secondary size-6" />
                Qualifications and Experience
              </h3>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="border-border shadow-sm rounded-lg">
                  <CardContent className="p-6">
                    <h4 className="font-default font-bold text-primary-500 text-lg mb-4">
                      Required Experience
                    </h4>

                    <ul className="space-y-3">
                      {requiredExperience.map((item, i) => <li key={i} className="flex items-start gap-3">
                          <CircleCheck className="size-5 text-secondary shrink-0 mt-0.5" />
                          <span className="text-muted-foreground font-default text-sm leading-relaxed">
                            {item}
                          </span>
                        </li>)}
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-border shadow-sm rounded-lg">
                  <CardContent className="p-6">
                    <h4 className="font-default font-bold text-primary-500 text-lg mb-4">
                      Strong Assets
                    </h4>

                    <ul className="space-y-3">
                      {strongAssets.map((item, i) => <li key={i} className="flex items-start gap-3">
                          <CircleCheck className="size-5 text-secondary shrink-0 mt-0.5" />
                          <span className="text-muted-foreground font-default text-sm leading-relaxed">
                            {item}
                          </span>
                        </li>)}
                    </ul>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-6 bg-secondary/10 border border-secondary/20 rounded-md p-5 flex items-start gap-3">
                <GraduationCap className="size-6 text-secondary shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-default font-bold text-primary-500 mb-2">
                    Formal credentials are not required
                  </h4>
                  <p className="text-muted-foreground font-default leading-relaxed">Designation or a university degree are not required for this role. Experience, judgment, follow-through, and problem-solving ability are valued more than formal credentials.</p>
                </div>
              </div>
            </div>
          </div>

          <aside className="lg:col-span-1 min-w-0">
            <Card className="border-border shadow-sm rounded-lg sticky top-8">
              <CardContent className="p-6 space-y-6">
                <div>
                  <p className="text-secondary font-default font-bold tracking-wider uppercase text-sm mb-3">
                    Role Snapshot
                  </p>

                  <h3 className="text-2xl font-default font-bold text-primary-500 mb-4">
                    Client Financial Administration Lead
                  </h3>

                  <p className="text-muted-foreground font-default leading-relaxed">
                    A full-time role focused on client financial operations,
                    reporting coordination, administrative follow-through,
                    funding support, process documentation, and practical
                    problem-solving.
                  </p>
                </div>

                <Separator className="bg-border" />

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="size-5 text-secondary shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-primary-500">
                        Location
                      </p>
                      <p className="text-sm text-muted-foreground">
                        1186 Roland St., Thunder Bay, Ontario
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <BriefcaseBusiness className="size-5 text-secondary shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-primary-500">
                        Employment Type
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Full-time, permanent
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <BadgeDollarSign className="size-5 text-secondary shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-primary-500">
                        Compensation
                      </p>
                      <p className="text-sm text-muted-foreground">
                        $80,000 to $90,000 per year
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <AlertCircle className="size-5 text-secondary shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-primary-500">
                        Work Model
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Primarily in-office in Thunder Bay. Some travel to
                        client communities may be required based on project
                        needs.
                      </p>
                    </div>
                  </div>
                </div>

                <Separator className="bg-border" />

                <div>
                  <p className="font-semibold text-primary-500 mb-2">
                    Reports To
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Senior Projects Manager
                  </p>
                </div>

                <Button size="lg" className="w-full bg-secondary hover:bg-secondary/90 text-white font-default" asChild>
                  <a href="mailto:employment@riverbendcorp.ca?subject=Application%20-%20Client%20Financial%20Administration%20Lead">
                    <Mail className="mr-2 size-5" />
                    Apply by Email
                  </a>
                </Button>
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>
    </section>;
}